<template>
  <div class="resumes-list">
    <div class="resumes-title">
      <div class="name">
        <span
          class="filterable"
          :class="{ active: sort === 'Name' || sort === '-Name' }"
          @click="sort !== 'Name' ? sortBy('Name') : sortBy('-Name')"
        >
          {{ $t('dashboard.filters.name') }}
          <ArrowFilter v-if="sort === '-Name'" class="filter-arrow" />
          <ArrowFilter v-else-if="sort === 'Name'" class="filter-arrow-turn" />
        </span>
      </div>

      <!-- mobile -->
      <div class="id-job">{{ $t('dashboard.filters.labels') }} / {{ $t('dashboard.filters.jobTitle') }}</div>
      <!-- desktop -->
      <div class="id">{{ $t('dashboard.filters.labels') }}</div>
      <div class="job">{{ $t('dashboard.filters.jobTitle') }}</div>

      <div class="date">
        <span
          class="filterable small"
          :class="{ active: sort === '-UpdatedAt' || sort === 'UpdatedAt' }"
          @click="sort !== '-UpdatedAt' ? sortBy('-UpdatedAt') : sortBy('UpdatedAt')"
        >
          <p class="ellipsis">{{ $t('dashboard.filters.date') }}</p>
          <ArrowFilter v-if="sort === '-UpdatedAt'" class="filter-arrow-turn" />
          <ArrowFilter v-else-if="sort === 'UpdatedAt'" class="filter-arrow" />
        </span>
      </div>
    </div>

    <div ref="resumes-container" class="resumes-container">
      <div
        v-for="(resume, index) in userResumes"
        :key="resume.resumes[0].id"
        class="item"
        :class="{
          active: openResumesGroup === resume.resumes[0].id,
          'previous-active': activeGroup - 1 === index,
        }"
        @click="isGrouped(resume) ? openCVGroup(resume.resumes[0].id, index) : null"
      >
        <div class="line" :class="{ sharedHover: isSharedIcon === resume.resumes[0].id }">
          <div v-if="!isGrouped(resume)" class="edit-button" :class="{ active: isGrouped(resume) }">
            <div
              v-clickout.stop="() => (editMenu === resume.resumes[0].id ? (editMenu = null) : false)"
              class="more-icon"
              @click.stop="openEditMenu(resume.resumes[0].id)"
            >
              <More />
              <EditPopin
                v-if="editMenu === resume.resumes[0].id"
                :resume="resume.resumes[0]"
                :workspace="workspace"
                @share="share"
                @update-labels="updateLabels"
                @move-resumes="moveResumes"
                @duplicate="duplicate"
                @remove="remove"
                @unshared="unshared"
              />
            </div>
          </div>
          <div v-else class="arrow">
            <arrow />
          </div>

          <router-link
            :to="{ name: 'editor', params: { id: resume.resumes[0].id } }"
            :event="isGrouped(resume) ? '' : 'click'"
            class="item-line"
          >
            <div class="image" :class="{ grouped: isGrouped(resume) }">
              <template v-if="isGrouped(resume)">
                <div
                  v-for="imgIndex in 3"
                  :key="imgIndex"
                  :class="!resume.resumes[0].candidate.logoLink ? colorPaletteLogo(resume.resumes[0].createdAt) : ''"
                >
                  <img
                    v-if="resume.resumes[0].candidate.logoLink"
                    :src="resume.resumes[0].candidate.logoLink"
                    alt="linkedin picture"
                  />
                </div>
              </template>

              <div :class="!resume.resumes[0].candidate.logoLink ? colorPaletteLogo(resume.resumes[0].createdAt) : ''">
                <img
                  v-if="resume.resumes[0].candidate.logoLink"
                  :src="resume.resumes[0].candidate.logoLink"
                  alt="candidate picture"
                />
                <p v-else>{{ resume.resumes[0].candidate.firstname[0] }}</p>
              </div>
            </div>

            <div class="info">
              <div class="name info-item">
                <div class="name-text ellipsis">
                  {{
                    `${resume.resumes[0].candidate.firstname.toLowerCase()} ${resume.resumes[0].candidate.lastname.toLowerCase()}`
                  }}
                </div>

                <span v-if="isGrouped(resume)" class="duplicated-resumes">
                  {{ resume.resumes.length }}
                </span>

                <div class="shared-popin-container">
                  <Men
                    v-if="resume.resumes.length < 2 && resume.resumes[0].isShared"
                    class="isShared-icon"
                    @mouseover="openIsShared(resume.resumes[0].id)"
                    @mouseleave="
                      isSharedIcon = null;
                      sharedInformation = { collabos: {} };
                      fetch = false;
                    "
                  />
                  <ShareTooltip
                    v-if="
                      isSharedIcon === resume.resumes[0].id &&
                      sharedInformation.collabos.length &&
                      resume.resumes.length < 2
                    "
                    :workspace="workspace"
                    :shared-information="sharedInformation"
                    :collaborators="collaborators"
                  />
                </div>
              </div>

              <!-- mobile -->
              <div class="id-job info-item">
                <p v-if="!isGrouped(resume) && resume.resumes[0].labels?.length > 0" class="identifier ellipsis">
                  {{ resume.resumes[0].labels.map(label => label.value).join(', ') }}
                </p>
                <p v-if="!isGrouped(resume)" class="job-title ellipsis">
                  {{ cleanJobTitle(resume.resumes[0].candidate.jobTitle) }}
                </p>
              </div>
              <!-- desktop -->
              <div class="id info-item labels ellipsis">
                <span v-if="!isGrouped(resume) && resume.resumes[0].labels?.length > 0" class="inline-label">{{
                  resume.resumes[0].labels[0].value
                }}</span>
                <span
                  v-if="!isGrouped(resume) && resume.resumes[0].labels?.length > 1"
                  class="inline-label"
                  :style="{ flexShrink: 0 }"
                  >+{{ resume.resumes[0].labels.length - 1 }}</span
                >
                <div v-if="!isGrouped(resume) && resume.resumes[0].labels?.length > 1" class="hover">
                  <span v-for="label in resume.resumes[0].labels" :key="label.id">
                    {{ label.value }}
                  </span>
                </div>
              </div>

              <div class="job info-item ellipsis">
                {{ isGrouped(resume) ? '' : cleanJobTitle(resume.resumes[0].candidate.jobTitle) }}
              </div>

              <div class="date info-item ellipsis">
                {{ formatDate(resume.resumes[0].updatedAt) }}
              </div>
            </div>
          </router-link>
        </div>

        <!-- For group of duplicated resumes-->
        <div v-if="openResumesGroup === resume.resumes[0].id" class="resumes-subgroup">
          <div v-for="duplicatedResume in resume.resumes" :key="duplicatedResume.id" class="subgroup-info-line">
            <div class="subgroup-edit-button">
              <div
                v-clickout.stop="() => (editMenu === duplicatedResume.id ? (editMenu = null) : false)"
                class="more-icon"
                @click.stop="openEditMenu(duplicatedResume.id)"
              >
                <More />
                <EditPopin
                  v-if="editMenu === duplicatedResume.id"
                  :resume="duplicatedResume"
                  :workspace="workspace"
                  @share="share"
                  @update-labels="updateLabels"
                  @move-resumes="moveResumes"
                  @duplicate="duplicate"
                  @remove="remove"
                  @unshared="unshared"
                />
              </div>
            </div>

            <router-link
              :to="{ name: 'editor', params: { id: duplicatedResume.id } }"
              class="resumes-subgroup-infos"
              :class="{ sharedHover: isSharedIcon === duplicatedResume.id }"
            >
              <div class="name info-item">
                <div class="name-text ellipsis">
                  {{ duplicatedResume.candidate.firstname.toLowerCase() }}
                </div>
                <div class="shared-popin-container">
                  <Men
                    v-if="duplicatedResume.isShared"
                    class="isShared-icon"
                    @mouseover="openIsShared(duplicatedResume.id, true)"
                    @mouseleave="
                      isSharedIcon = null;
                      sharedInformation = { collabos: {} };
                    "
                  />
                  <ShareTooltip
                    v-if="isSharedIcon === duplicatedResume.id && sharedInformation.collabos.length"
                    :workspace="workspace"
                    :shared-information="sharedInformation"
                    :collaborators="collaborators"
                  />
                </div>
              </div>

              <!-- mobile -->
              <div class="id-job info-item">
                <p v-if="duplicatedResume.labels?.length > 0" class="identifier ellipsis">
                  {{ duplicatedResume.labels.map(label => label.value).join(', ') }}
                </p>
                <p class="job-title ellipsis">
                  {{ cleanJobTitle(duplicatedResume.candidate.jobTitle) }}
                </p>
              </div>
              <!-- desktop -->
              <div class="id info-item labels ellipsis">
                <span v-if="duplicatedResume.labels?.length > 0" class="inline-label">{{
                  duplicatedResume.labels[0].value
                }}</span>
                <span v-if="duplicatedResume.labels?.length > 1" class="inline-label" :style="{ flexShrink: 0 }"
                  >+{{ duplicatedResume.labels.length - 1 }}</span
                >
                <div v-if="duplicatedResume.labels?.length > 1" class="hover">
                  <span v-for="label in duplicatedResume.labels" :key="label.id">
                    {{ label.value }}
                  </span>
                </div>
              </div>
              <div class="job info-item ellipsis">
                {{ cleanJobTitle(duplicatedResume.candidate.jobTitle) }}
              </div>

              <div class="date info-item ellipsis">
                {{ formatDate(duplicatedResume.updatedAt) }}
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/libraries/formatter';
import { mapGetters } from 'vuex';

import Mixin from './dashboard-mixin';
import ShareTooltip from './ShareTooltip';
import EditPopin from './EditPopin';

export default {
  name: 'ResumesList',
  components: {
    ShareTooltip,
    EditPopin,
  },
  mixins: [Mixin],
  props: {
    workspace: {
      type: Number,
      default: 0,
    },
  },
  emits: ['scroll'],
  data() {
    return {
      formatDate,
      activeGroup: null,
    };
  },
  computed: {
    ...mapGetters(['resumes', 'user', 'isFetching']),
    userResumes() {
      return this.resumes.items;
    },
  },
  mounted() {
    const container = this.$refs['resumes-container'];
    container.onscroll = () => {
      this.$emit('scroll', container);
    };
  },
  methods: {
    isGrouped(resume) {
      return resume.resumes.length > 1;
    },
    cleanJobTitle(jobTitle) {
      const div = document.createElement('div');
      div.innerHTML = jobTitle;
      return div.textContent || div.innerText || '';
    },
    openCVGroup(id, index) {
      if (this.openResumesGroup === id) {
        this.openResumesGroup = null;
        this.activeGroup = null;
      } else {
        this.openResumesGroup = id;
        this.activeGroup = index;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive/dashboard.scss';

.resumes-list {
  border-radius: 7px;
  box-shadow: 0 1px 7px 0 #dddddd;
  margin-bottom: 30px;
  padding-bottom: 10px;
  background-color: colors.$white-color;
  max-width: 1000px;
}
.resumes-title {
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 42px;
  margin-bottom: 10px;
  background-color: colors.$background-color;
  border-radius: 7px 7px 0 0;
  .id-job {
    display: none;
  }
  div:first-child {
    padding-left: 130px;
  }
  div:last-child {
    padding-right: 28px;
  }
  div {
    max-width: 254px;
    font-family: Lato, sans-serif;
    font-size: 11px;
    font-weight: bold;
    line-height: 13px;
    color: colors.$light-text-color;
  }
  .name,
  .id,
  .job {
    width: 30%;
  }
  .date {
    width: 10%;
  }
  .filterable {
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: colors.$white-color;
    border-radius: 4px;
    padding: 0 10px;
    height: 21px;
    box-shadow: 0 1px 4px 0 rgba(186, 186, 186, 0.5);
    width: 44px;
    &.small {
      width: 60px;
    }
    &.active {
      color: colors.$text-color;
      border: 1px solid colors.$secondary-color;
      width: 57px;
      padding: 0 9px;
      &.small {
        width: 72px;
      }
    }
    &:hover {
      color: colors.$text-color;
    }
  }
  .filter-arrow-turn {
    transform: rotate(180deg);
    margin-top: 1px;
  }
  .filter-arrow {
    margin: 1px 0 0 5px;
  }
}
.item {
  position: relative;
  margin: 0 28px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:not(:last-child) {
    border-bottom: solid 1px #d0d0d0;
  }
  &.active {
    border: solid 1px colors.$secondary-color;
    border-radius: 4px;
    margin-bottom: 10px;
    background-color: colors.$lighter-yellow-color;
    .line,
    .line:hover {
      background-color: colors.$secondary-color;
      color: colors.$white-color;
      div,
      span {
        font-weight: 700;
      }
      .item-line:hover {
        background-color: colors.$secondary-color;
        color: colors.$white-color;
      }
      .arrow {
        transform: rotate(180deg);
        justify-content: flex-end;
        padding-right: 4px;
        &:hover {
          background-color: colors.$secondary-color;
          color: colors.$white-color;
          & + div {
            background-color: colors.$secondary-color;
            color: colors.$white-color;
          }
        }
      }
    }
  }
  &.previous-active {
    border-bottom: none;
  }
  .line {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
    &:hover {
      background-color: colors.$hover-background-color;
      color: colors.$secondary-color;
      div,
      span {
        font-weight: 700;
      }
      &.sharedHover {
        background-color: colors.$white-color;
        .item-line {
          color: colors.$text-color !important;
          .job,
          .date {
            font-weight: 300;
          }
        }
      }
    }
    .edit-button:hover {
      background-color: colors.$white-color;
      .more-icon {
        background-color: colors.$light-blue-color;
        color: colors.$white-color;
      }
      & + a {
        background-color: colors.$white-color;
        color: colors.$text-color;
        .job,
        .date {
          font-weight: 200;
        }
      }
    }
  }
  .edit-button {
    display: flex;
    align-items: center;
    width: 52px;
    height: 42px;
    .more-icon {
      position: relative;
      left: 5px;
      height: 31px;
      width: 31px;
      border-radius: 50%;
      color: colors.$light-blue-color;
      &:hover {
        background-color: colors.$light-blue-color;
        color: colors.$white-color;
      }
    }
  }
  .arrow {
    box-sizing: border-box;
    width: 51px;
    height: 42px;
    padding-left: 5px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    &:hover {
      color: colors.$secondary-color;
      background-color: colors.$hover-background-color;
      & + div {
        color: colors.$secondary-color;
        background-color: colors.$hover-background-color;
      }
    }
  }
  .item-line {
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 100%;
    height: 42px;
    box-sizing: border-box;
    &:hover {
      border-radius: 2px;
      color: colors.$hover-text-color;
      div {
        font-weight: 700;
      }
    }
  }
  .info {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
  }
  .id-job {
    display: none;
  }
  .image {
    justify-content: center;
    width: 52px;
    text-transform: uppercase;
    .grey {
      background: #384547;
    }
    .blue {
      background: #416d85;
    }
    .green {
      background: #40a798;
    }
    .light-green {
      background: #a3c9c7;
    }
    .purple {
      background: #947b89;
    }
    .red {
      background: #c72c41;
    }
    .yellow {
      background: #d1962e;
    }
    .orange {
      background: #ffa96a;
    }

    div {
      border: 1px solid colors.$lightest-grey-color;
      height: 31px;
      width: 31px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      font-weight: 700;
      color: colors.$white-color;
      overflow: hidden;
    }
    img {
      background-size: cover;
      width: 100%;
    }
  }
  .image {
    &.grouped {
      display: flex;
      position: relative;
      align-items: center;
      justify-content: flex-start;
      div {
        position: absolute;
        &:nth-child(1) {
          left: -12px;
        }
        &:nth-child(2) {
          left: -8px;
        }
        &:nth-child(3) {
          left: -4px;
        }
      }
    }
  }
  .info-item {
    padding: 0 20px 0 0;
    box-sizing: border-box;
    max-width: 254px;
    flex-shrink: 0;
  }
  .name {
    width: 30%;
    display: flex;
    align-items: center;
    font-size: 14px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    line-height: 17px;
    text-transform: capitalize;

    .name-text {
      max-width: calc(100% - 50px);
    }

    .duplicated-resumes {
      font-weight: 300;
      margin-left: 5px;
    }
    .isShared-icon {
      &:hover {
        color: colors.$light-blue-color !important;
      }
    }
    .shared-popin-container {
      position: relative;
      width: 42px;
      height: 42px;
    }
  }
  .id {
    width: 30%;
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    line-height: 13px;
  }

  .labels {
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    color: black;
    display: flex;
    gap: 4px;
    position: relative;
    overflow: visible;

    .inline-label {
      padding: 5px;
      border: colors.$black-color 1px solid;
      border-radius: 3px;
      background-color: colors.$white-color;
      min-width: 0;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    &:hover .hover,
    .hover:hover {
      opacity: 1;
      z-index: 999;
      transition: opacity 0.2s ease-in-out 0.3s;
    }

    .hover {
      position: absolute;
      top: 30px;

      display: flex;
      flex-direction: column;
      gap: 8px;

      width: calc(100% - 48px);
      max-height: 128px;
      padding: 8px 12px;

      overflow-y: auto;

      opacity: 0;
      border: #121a2e 1px solid;
      border-radius: 3px;
      background-color: #fefefe;
      transition: opacity 0.2s ease-in-out;

      > span {
        white-space: wrap;
      }
    }
  }

  .job {
    width: 30%;
  }
  .date {
    width: 10%;
  }
  .job,
  .date {
    font-size: 11px;
    font-family: Lato, sans-serif;
    font-weight: 300;
    line-height: 13px;
  }
  .resumes-subgroup {
    display: flex;
    flex-direction: column;
    width: 100%;
    .subgroup-info-line {
      position: relative;
      cursor: pointer;
      display: flex;
      align-items: center;
      width: auto;
      height: 40px;
      margin: 0 20px;
      &:not(:first-child) {
        border-top: 1px solid #d0d0d0;
      }
      .edit-popin {
        top: 44px;
        left: 26px;
      }
    }
    .resumes-subgroup-infos {
      display: flex;
      align-items: center;
      width: 100%;
      &:not(:last-child) {
        border-bottom: solid 1px #d0d0d0;
      }
      &:hover {
        color: colors.$hover-text-color;
        border-radius: 2px;
        div {
          font-weight: 700;
        }
        &.sharedHover {
          color: colors.$text-color !important;
          .job,
          .date {
            font-weight: 300;
          }
        }
      }
      .shared-popin-container {
        position: relative;
      }
      .name,
      .id {
        width: 31%;
      }
      .job {
        width: 30.5%;
      }
      .date {
        width: 8%;
        padding: 0;
      }
    }
    .resumes-subgroup-infos div:not(.edit-popin) {
      font-size: 11px;
      align-items: center;
    }
    .subgroup-edit-button {
      margin-left: 28px;
      width: 56px;
      color: colors.$light-blue-color;
      .more-icon {
        height: 31px;
        width: 31px;
        border-radius: 50%;
        &:hover {
          background-color: colors.$light-blue-color;
          color: colors.$white-color;
        }
      }
    }
    .info-item {
      margin-left: -2px;
    }
  }
}
</style>
